import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ClipboardCheck, FileText } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { listPendingProvisionsByTenant, reviewProvision } from "@/lib/api/provisions";
import { groupProvisionsByDocument } from "@/lib/review-queue";
import { ProvisionCard } from "@/pages/tenant/document-review";
import { useRole } from "@/lib/auth/use-role";
import { useAuth } from "@/lib/auth/auth-provider";
import { enforceMfnForFund, describeMfnDelta } from "@/lib/pipeline/continuous-mfn";
import { Badge, Card, EmptyState, PageHeader } from "@/components/ui/primitives";

function ReviewQueueInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const { canWrite, role } = useRole(tenant.id);
  const canReview = canWrite || role === "reviewer";
  const qc = useQueryClient();

  const {
    data: pending,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["review-queue", tenant.id],
    queryFn: () => listPendingProvisionsByTenant(tenant.id),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["review-queue", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["provisions"] });
    void qc.invalidateQueries({ queryKey: ["provision-counts", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["documents", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["mfn-triggers", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["portfolio-radar", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["obligations", tenant.id] });
  };

  const review = useMutation({
    mutationFn: (args: { id: string; patch: Parameters<typeof reviewProvision>[2] }) => {
      if (!user) throw new Error("Not signed in.");
      return reviewProvision(args.id, user.id, args.patch);
    },
    onSuccess: (row) => {
      // Continuous MFN enforcement: a reviewed provision changes the facts
      // the engine reasons over. Fire-and-forget — never block or fail the
      // review on enforcement. Toast only when something actually changed.
      // 2026-07-03 audit fix: enforceMfnForFund can now throw on a genuine
      // core-step failure — log it instead of swallowing to `undefined` a
      // second time, so a real failure still leaves a trace even though the
      // review itself must not be blocked by it.
      enforceMfnForFund({
        tenantId: tenant.id,
        fundId: row.fund_id,
        source: "provision_reviewed",
        sourceRef: row.id,
        userId: user?.id,
      })
        .then((summary) => {
          const delta = describeMfnDelta(summary);
          if (delta) toast.success("MFN re-evaluated", { description: delta });
        })
        .catch((err: unknown) => {
          console.error(
            `[review-queue] continuous MFN enforcement failed for fund=${row.fund_id}:`,
            err,
          );
        });
    },
    onSettled: invalidate,
  });

  const groups = groupProvisionsByDocument(pending ?? []);
  const pendingCount = pending?.length ?? 0;

  return (
    <>
      <PageHeader
        title="Review queue"
        subtitle="Nothing enters your MFN engine or compliance calendar until a human accepts it."
        actions={
          pendingCount > 0 ? (
            <Badge tone="risk">{pendingCount} pending</Badge>
          ) : (
            <Badge tone="clear">Queue clear</Badge>
          )
        }
      />

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading review queue…</p>
      ) : error ? (
        <div className="rounded-lg border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck className="size-8" />}
          title="Queue clear — every extracted provision has been reviewed."
          description="New extractions land here first. Nothing feeds the MFN engine or the compliance calendar until a human confirms it."
        />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <Card key={g.documentId} className="p-4">
              <div className="mb-3 flex items-center justify-between gap-2 border-b border-neutral-800/70 pb-2.5">
                <Link
                  to="/t/$slug/documents/$documentId"
                  params={{ slug: tenant.slug, documentId: g.documentId }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-100 hover:underline"
                >
                  <FileText className="size-3.5 text-neutral-500" /> {g.documentTitle}
                </Link>
                <span className="text-[11px] tabular-nums text-neutral-500">
                  {g.provisions.length} pending
                </span>
              </div>
              <div className="space-y-2">
                {g.provisions.map((p) => (
                  <ProvisionCard
                    key={p.id}
                    provision={p}
                    canReview={canReview}
                    selected={false}
                    busy={review.isPending}
                    onSelect={() => undefined}
                    onReview={(patch) => review.mutate({ id: p.id, patch })}
                  />
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

export function ReviewQueuePage() {
  return (
    <TenantLayout>
      <ReviewQueueInner />
    </TenantLayout>
  );
}
